import { useState, useMemo, useCallback, useRef, useEffect, createContext, useContext } from 'react';
import { Link } from 'react-router-dom';
import type { GridApi, ColDef, ICellRendererParams } from '@ag-grid-community/core';
import { useQuery, useMutation } from 'convex/react';
import { format, formatDistanceToNow, subDays, startOfDay, endOfDay, startOfMonth, startOfYear } from 'date-fns';
import {
  Sparkles,
  RotateCcw,
  ArrowRightLeft,
  ShieldCheck,
  ShieldOff,
  BadgeCheck,
  BadgeX,
  Globe,
  Trash2,
  Loader2,
  CalendarDays,
  Activity,
  TrendingUp,
  Filter,
  RotateCw,
  Pencil,
  Check,
  X,
  ListTodo,
  ExternalLink,
  FileSpreadsheet,
  Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import { useDebouncedCallback } from 'use-debounce';
import { utils, writeFile } from 'xlsx';
import AGDataGrid from '@/components/ag-data-grid';
import { useBreadcrumb } from '@/components/breadcrumb-provider';
import Container from '@/components/container';
import { CreateTaskDialog } from '@/components/create-task-dialog';
import { OverrideCell } from '@/components/monitor/override-cell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useGrid } from '@/contexts/grid-context';
import { isInRegion, REGIONS } from '@/lib/region-utils';
import type { Region } from '@/lib/region-utils';
import { cn } from '@/lib/utils';
import { api } from '../../../../../convex/_generated/api';

// ============================================================================
// Types
// ============================================================================

type EventKind
  = | 'new_live'
    | 'previous_live'
    | 'change_live'
    | 'redirect'
    | 'override'
    | 'useless_override'
    | 'badge_detected'
    | 'badge_removed'
    | 'new_domain'
    | 'domain_removed';

type MonitorStatus = 'live' | 'parked' | 'redirect_in_zone' | 'redirect_out_of_zone' | 'error';

interface ScanHistoryEntry {
  status: string;
  scannedAt: number;
}

// Context to provide scan history to cell renderers
type ScanHistoryMap = Record<string, ScanHistoryEntry[]>;
const ScanHistoryContext = createContext<ScanHistoryMap>({});

interface MonitorEvent {
  _id: string;
  domain: string;
  scanId?: string;
  kind: EventKind;
  fromStatus?: MonitorStatus;
  toStatus?: MonitorStatus;
  message?: string;
  createdAt: number;
}

interface EventRow {
  id: string;
  domain: string;
  kind: EventKind;
  fromStatus: MonitorStatus | null;
  toStatus: MonitorStatus | null;
  message: string;
  createdAt: number;
  timeAgo: string;
  // Domain status fields
  state: string | null;
  country: string | null;
  registrationDate: number | null;
  liveSince: number | null;
  launchVelocityDays: number | null;
  currentStatus: MonitorStatus;
  hasActiveOverride: boolean;
  overrideStatus: MonitorStatus | null;
  // For 7-day history
  highWaterStatus: MonitorStatus | null;
  graceExpiresAt: number | null;
}

// ============================================================================
// Event Kind Configuration
// ============================================================================

const EVENT_KIND_CONFIG: Record<EventKind, {
  label: string;
  icon: React.ElementType;
  className: string;
  description: string;
}> = {
  new_live: {
    label: 'New Live',
    icon: Sparkles,
    className: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100',
    description: 'Domain went live for the first time',
  },
  previous_live: {
    label: 'Returned Live',
    icon: RotateCw,
    className: 'bg-green-100 text-green-800 hover:bg-green-100',
    description: 'Domain returned to live status',
  },
  change_live: {
    label: 'Left Live',
    icon: TrendingUp,
    className: 'bg-orange-100 text-orange-800 hover:bg-orange-100',
    description: 'Domain changed away from live status',
  },
  redirect: {
    label: 'Redirect',
    icon: ArrowRightLeft,
    className: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
    description: 'Redirect status changed',
  },
  override: {
    label: 'Override',
    icon: ShieldCheck,
    className: 'bg-purple-100 text-purple-800 hover:bg-purple-100',
    description: 'Manual override applied',
  },
  useless_override: {
    label: 'Useless Override',
    icon: ShieldOff,
    className: 'bg-slate-100 text-slate-800 hover:bg-slate-100',
    description: 'Override matches scan result',
  },
  badge_detected: {
    label: 'Badge Detected',
    icon: BadgeCheck,
    className: 'bg-teal-100 text-teal-800 hover:bg-teal-100',
    description: 'Cybersecurity badge detected',
  },
  badge_removed: {
    label: 'Badge Removed',
    icon: BadgeX,
    className: 'bg-rose-100 text-rose-800 hover:bg-rose-100',
    description: 'Cybersecurity badge no longer detected',
  },
  new_domain: {
    label: 'New Domain',
    icon: Globe,
    className: 'bg-cyan-100 text-cyan-800 hover:bg-cyan-100',
    description: 'New domain added to zone file',
  },
  domain_removed: {
    label: 'Domain Removed',
    icon: Trash2,
    className: 'bg-red-100 text-red-800 hover:bg-red-100',
    description: 'Domain removed from zone file',
  },
};

const STATUS_COLORS: Record<string, string> = {
  live: 'bg-green-100 text-green-800 hover:bg-green-100',
  parked: 'bg-gray-100 text-gray-800 hover:bg-gray-100',
  redirect_in_zone: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
  redirect_out_of_zone: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100',
  error: 'bg-red-100 text-red-800 hover:bg-red-100',
};

// Curated order for the event-type quick filter chips (mirrors prior tab order)
const EVENT_KIND_ORDER: EventKind[] = [
  'new_live',
  'previous_live',
  'change_live',
  'redirect',
  'override',
  'new_domain',
  'domain_removed',
  'badge_detected',
  'badge_removed',
];

const TLD_OPTIONS: { value: string; label: string }[] = [
  { value: 'bank', label: '.Bank' },
  { value: 'insurance', label: '.Insurance' },
];

// Toggle a value in/out of a selection array
function toggleValue<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

function formatDateRangeLabel(start: Date | undefined, end: Date | undefined): string {
  if (start && end)
    return `${format(start, 'MMM d')} – ${format(end, 'MMM d')}`;
  if (start)
    return `From ${format(start, 'MMM d')}`;
  if (end)
    return `Until ${format(end, 'MMM d')}`;
  return 'Custom range';
}

type DatePreset = 'today' | '7days' | '30days' | 'mtd' | 'ytd' | 'all';

const DATE_PRESET_OPTIONS: { value: DatePreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7days', label: 'Last 7 Days' },
  { value: '30days', label: 'Last 30 Days' },
  { value: 'mtd', label: 'Month to Date' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'all', label: 'All Time' },
];

// Status colors for 7-day history and override dots
const HISTORY_STATUS_COLORS: Record<string, { bg: string; label: string }> = {
  live: { bg: 'bg-green-500', label: 'Live' },
  parked: { bg: 'bg-slate-400', label: 'Parked' },
  redirect_in_zone: { bg: 'bg-blue-500', label: 'Redirect (In Zone)' },
  redirect_out_of_zone: { bg: 'bg-amber-500', label: 'Redirect (Out of Zone)' },
  error: { bg: 'bg-red-500', label: 'Error' },
  timeout: { bg: 'bg-red-400', label: 'Timeout' },
  incomplete: { bg: 'bg-red-300', label: 'Incomplete' },
};

const STATUS_PRIORITY: Record<string, number> = {
  live: 5,
  redirect_in_zone: 4,
  redirect_out_of_zone: 3,
  parked: 2,
  error: 1,
  timeout: 1,
  incomplete: 1,
};

// ============================================================================
// Components
// ============================================================================

/** Single day box with its own tooltip */
function DayBox({
  date,
  scans,
  bestStatus,
  isToday,
}: {
  date: Date;
  scans: ScanHistoryEntry[];
  bestStatus: string | null;
  isToday: boolean;
}) {
  const hasScans = scans.length > 0;
  const colorStyle = bestStatus ? HISTORY_STATUS_COLORS[bestStatus]?.bg ?? 'bg-gray-300' : '';
  const statusLabel = bestStatus ? HISTORY_STATUS_COLORS[bestStatus]?.label ?? bestStatus : null;

  return (
    <TooltipProvider>
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <div
            className={`h-5 w-4 ${
              hasScans
                ? colorStyle
                : 'border border-dashed border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-900'
            } ${isToday ? 'ring-1 ring-foreground/30' : ''}`}
          />
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <div className="font-medium">{format(date, 'EEE, MMM d')}</div>
          {hasScans
            ? (
                <>
                  <div className="text-muted-foreground">{statusLabel}</div>
                  {scans.length > 1 && (
                    <div className="text-muted-foreground">
                      {scans.length}
                      {' '}
                      scans
                    </div>
                  )}
                </>
              )
            : (
                <div className="text-muted-foreground">No scan</div>
              )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * 7-Day Timeline - square boxes, each with individual hover
 * Reads scan history from context (fetched for visible rows)
 */
function ScanHistoryCell({
  domain,
  highWaterStatus,
  graceExpiresAt,
  currentStatus,
}: {
  domain: string;
  highWaterStatus: MonitorStatus | null;
  graceExpiresAt: number | null;
  currentStatus: MonitorStatus;
}) {
  const scanHistoryMap = useContext(ScanHistoryContext);
  const scans = scanHistoryMap[domain] || [];

  const DAY_MS = 24 * 60 * 60 * 1000;

  // Get today at midnight
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Build 7-day buckets (index 0 = 6 days ago, index 6 = today)
  const dayBuckets: Array<{ date: Date; scans: ScanHistoryEntry[]; bestStatus: string | null }> = [];
  for (let i = 6; i >= 0; i--) {
    const dayDate = new Date(today);
    dayDate.setDate(dayDate.getDate() - i);

    const dayStart = dayDate.getTime();
    const dayEnd = dayStart + DAY_MS;

    const dayScans = scans.filter((s) => s.scannedAt >= dayStart && s.scannedAt < dayEnd);

    // Find best status for this day (highest priority)
    let bestStatus: string | null = null;
    let bestPriority = 0;
    for (const scan of dayScans) {
      const priority = STATUS_PRIORITY[scan.status] ?? 0;
      if (priority > bestPriority) {
        bestPriority = priority;
        bestStatus = scan.status;
      }
    }

    dayBuckets.push({ date: dayDate, scans: dayScans, bestStatus });
  }

  // Grace period status
  const isInGracePeriod = graceExpiresAt && graceExpiresAt > Date.now();
  const isProtected = isInGracePeriod && currentStatus !== highWaterStatus && highWaterStatus;

  // If no history loaded yet, show loading state
  if (!scanHistoryMap[domain]) {
    return (
      <div className="flex h-full items-center">
        <div className="flex items-center gap-0.5">
          {Array.from({ length: 7 }).map((_, idx) => (
            <div
              key={idx}
              className="h-5 w-4 bg-gray-100 dark:bg-gray-800"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full items-center gap-2">
      {/* 7-day boxes */}
      <div className="flex items-center gap-0.5">
        {dayBuckets.map((bucket, idx) => (
          <DayBox
            key={bucket.date.getTime()}
            date={bucket.date}
            scans={bucket.scans}
            bestStatus={bucket.bestStatus}
            isToday={idx === 6}
          />
        ))}
      </div>

      {/* Grace indicator */}
      {isProtected && (
        <TooltipProvider>
          <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
              <div className="h-5 border-l border-amber-400 bg-amber-100 px-1.5 text-[10px] font-medium leading-5 text-amber-700 dark:border-amber-600 dark:bg-amber-900/40 dark:text-amber-400">
                GRACE
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              <div className="font-medium">Grace Period Active</div>
              <div className="text-muted-foreground">
                Protected at "
                {HISTORY_STATUS_COLORS[highWaterStatus]?.label}
                " until
              </div>
              <div>{format(new Date(graceExpiresAt), 'MMM d, h:mm a')}</div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

function EventKindBadge({ kind }: { kind: EventKind }) {
  const config = EVENT_KIND_CONFIG[kind];
  const Icon = config.icon;

  return (
    <TooltipProvider>
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <Badge className={cn('gap-1.5', config.className)}>
            <Icon className="size-3" />
            {config.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p>{config.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status)
    return <span className="text-muted-foreground">—</span>;

  const colorClass = STATUS_COLORS[status] || STATUS_COLORS.error;
  const label = status.replaceAll('_', ' ').replaceAll(/\b\w/g, (l) => l.toUpperCase());

  return (
    <Badge className={colorClass}>
      {label}
    </Badge>
  );
}

function StatusTransition({ from, to }: { from: MonitorStatus | null; to: MonitorStatus | null }) {
  if (!from && !to) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <div className="flex h-full items-center gap-2">
      {from && <StatusBadge status={from} />}
      {from && to && <span className="text-muted-foreground">→</span>}
      {to && <StatusBadge status={to} />}
    </div>
  );
}

function VelocityCell({
  domain,
  velocityDays,
  onUpdate,
}: {
  domain: string;
  velocityDays: number | null;
  onUpdate: (domain: string, velocity: number | null) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const startEditing = () => {
    setEditValue(velocityDays?.toString() ?? '');
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmed = editValue.trim();
    if (trimmed === '') {
      onUpdate(domain, null);
    } else {
      const parsed = Number.parseInt(trimmed, 10);
      if (!Number.isNaN(parsed) && parsed >= 0) {
        onUpdate(domain, parsed);
      }
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex h-full items-center gap-1">
        <input
          ref={inputRef}
          type="number"
          min="0"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter')
              handleSave();
            else if (e.key === 'Escape')
              handleCancel();
          }}
          className="h-6 w-14 rounded border border-input bg-background px-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <Button variant="ghost" size="sm" className="size-6 p-0 text-green-600 hover:bg-green-100" onClick={handleSave}>
          <Check className="size-3" />
        </Button>
        <Button variant="ghost" size="sm" className="size-6 p-0 text-red-600 hover:bg-red-100" onClick={handleCancel}>
          <X className="size-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="group flex h-full items-center gap-1">
      <span className="text-sm">{velocityDays === null ? '—' : `${velocityDays}d`}</span>
      <Button variant="ghost" size="sm" className="size-5 p-0 opacity-0 transition-opacity group-hover:opacity-100" onClick={startEditing}>
        <Pencil className="size-3 text-muted-foreground" />
      </Button>
    </div>
  );
}

function LiveSinceCell({
  domain,
  liveSince,
  onUpdate,
}: {
  domain: string;
  liveSince: number | null;
  onUpdate: (domain: string, liveSince: number | null) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const startEditing = () => {
    if (liveSince) {
      const date = new Date(liveSince);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      setEditValue(`${year}-${month}-${day}`);
    } else {
      setEditValue('');
    }
    setIsEditing(true);
  };

  const handleSave = () => {
    if (editValue.trim() === '') {
      onUpdate(domain, null);
    } else {
      const [year, month, day] = editValue.split('-').map(Number);
      const parsed = new Date(year, month - 1, day, 12, 0, 0);
      if (!Number.isNaN(parsed.getTime())) {
        onUpdate(domain, parsed.getTime());
      }
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex h-full items-center gap-1">
        <input
          ref={inputRef}
          type="date"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter')
              handleSave();
            else if (e.key === 'Escape')
              handleCancel();
          }}
          className="h-6 w-28 rounded border border-input bg-background px-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <Button variant="ghost" size="sm" className="size-6 p-0 text-green-600 hover:bg-green-100" onClick={handleSave}>
          <Check className="size-3" />
        </Button>
        <Button variant="ghost" size="sm" className="size-6 p-0 text-red-600 hover:bg-red-100" onClick={handleCancel}>
          <X className="size-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="group flex h-full items-center gap-1">
      <span className="text-sm">{liveSince ? format(new Date(liveSince), 'MMM d, yyyy') : '—'}</span>
      <Button variant="ghost" size="sm" className="size-5 p-0 opacity-0 transition-opacity group-hover:opacity-100" onClick={startEditing}>
        <Pencil className="size-3 text-muted-foreground" />
      </Button>
    </div>
  );
}

// ============================================================================
// Main Page Component
// ============================================================================

function MonitorEventsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedKinds, setSelectedKinds] = useState<EventKind[]>([]);
  const [selectedTlds, setSelectedTlds] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<Region[]>([]);
  const [copiedCount, setCopiedCount] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>(() => subDays(new Date(), 7));
  const [endDate, setEndDate] = useState<Date | undefined>(() => new Date());
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskDialogData, setTaskDialogData] = useState<{ domain: string; status: string } | null>(null);
  const [visibleDomains, setVisibleDomains] = useState<string[]>([]);
  const gridApiRef = useRef<GridApi<EventRow> | null>(null);
  const { registerGridApi } = useGrid();
  const { setLabel, removeLabel } = useBreadcrumb();

  // Set breadcrumb
  useEffect(() => {
    const path = '/app/monitor/events';
    setLabel(path, { name: 'Monitor Events', path });
    return () => removeLabel(path);
  }, [setLabel, removeLabel]);

  // Fetch events
  // NOTE: Server defaults to last 7 days unless we explicitly request otherwise.
  // We pass the selected range (or allTime) so older-than-7-days filters can actually fetch data.
  const allEventsQueryArgs = useMemo(() => {
    if (!startDate && !endDate) {
      return { limit: 1000, allTime: true as const };
    }
    return {
      limit: 1000,
      startTime: startDate ? startOfDay(startDate).getTime() : undefined,
      endTime: endDate ? endOfDay(endDate).getTime() : undefined,
    };
  }, [startDate, endDate]);

  const allEvents = useQuery(api.monitor.getActivityFeed, allEventsQueryArgs);
  // Fetch domain statuses to get accurate launch velocity (editable field)
  const domainStatuses = useQuery(api.monitor.getAllDomainStatuses, { onlyMonitored: false });
  const overrides = useQuery(api.monitor.getAllActiveOverrides);

  // Query scan history for visible domains only
  const scanHistory = useQuery(
    api.monitor.getScanHistoryForDomains,
    visibleDomains.length > 0 ? { domains: visibleDomains } : 'skip',
  );
  const scanHistoryMap: ScanHistoryMap = scanHistory ?? {};

  // Mutations
  const upsertOverride = useMutation(api.monitor.upsertStatusOverride);
  const deactivateOverride = useMutation(api.monitor.deactivateOverride);
  const updateVelocity = useMutation(api.monitor.updateLaunchVelocity);
  const updateLiveSinceMutation = useMutation(api.monitor.updateLiveSince);

  const isLoading = !allEvents;

  // Build override map
  const overrideMap = useMemo(() => {
    const map = new Map<string, MonitorStatus>();
    if (overrides) {
      for (const o of overrides) {
        map.set(o.domain, o.statusOverride as MonitorStatus);
      }
    }
    return map;
  }, [overrides]);

  // Build a map of domain -> full status data for quick lookup
  const domainStatusMap = useMemo(() => {
    const map = new Map<string, {
      state: string | null;
      country: string | null;
      registrationDate: number | null;
      liveSince: number | null;
      launchVelocityDays: number | null;
      currentStatus: MonitorStatus;
      highWaterStatus: MonitorStatus | null;
      graceExpiresAt: number | null;
    }>();
    if (domainStatuses) {
      for (const d of domainStatuses) {
        map.set(d.domain, {
          state: d.state,
          country: d.country,
          registrationDate: d.registrationDate,
          liveSince: d.liveSince ?? null,
          launchVelocityDays: d.launchVelocityDays ?? null,
          currentStatus: d.currentStatus as MonitorStatus,
          highWaterStatus: (d.highWaterStatus as MonitorStatus) ?? null,
          graceExpiresAt: d.graceExpiresAt ?? null,
        });
      }
    }
    return map;
  }, [domainStatuses]);

  // Override handlers
  const handleSetOverride = useCallback(async (domain: string, status: MonitorStatus) => {
    try {
      await upsertOverride({ domain, statusOverride: status, active: true });
      toast.success(`Override set to "${status.replaceAll('_', ' ')}" for ${domain}`);
    } catch {
      toast.error('Failed to set override');
    }
  }, [upsertOverride]);

  const handleClearOverride = useCallback(async (domain: string) => {
    try {
      await deactivateOverride({ domain });
      toast.success(`Override cleared for ${domain}`);
    } catch {
      toast.error('Failed to clear override');
    }
  }, [deactivateOverride]);

  // Velocity update handler
  const handleUpdateVelocity = useCallback(async (domain: string, velocity: number | null) => {
    try {
      await updateVelocity({ domain, launchVelocityDays: velocity });
      toast.success(`Launch velocity updated for ${domain}`);
    } catch {
      toast.error('Failed to update launch velocity');
    }
  }, [updateVelocity]);

  // Live since update handler
  const handleUpdateLiveSince = useCallback(async (domain: string, liveSince: number | null) => {
    try {
      await updateLiveSinceMutation({ domain, liveSince });
      toast.success(`Live since date updated for ${domain}`);
    } catch {
      toast.error('Failed to update live since date');
    }
  }, [updateLiveSinceMutation]);

  // Task dialog handler
  const handleOpenTaskDialog = useCallback((domain: string, status: string) => {
    setTaskDialogData({ domain, status });
    setTaskDialogOpen(true);
  }, []);

  // Debounced handler to update visible domains when viewport changes
  const updateVisibleDomains = useDebouncedCallback(() => {
    if (!gridApiRef.current)
      return;

    const renderedNodes: string[] = [];
    gridApiRef.current.forEachNodeAfterFilterAndSort((node) => {
      if (node.data && node.rowIndex !== null) {
        const firstRow = gridApiRef.current!.getFirstDisplayedRowIndex();
        const lastRow = gridApiRef.current!.getLastDisplayedRowIndex();
        if (node.rowIndex >= firstRow && node.rowIndex <= lastRow) {
          renderedNodes.push(node.data.domain);
        }
      }
    });

    setVisibleDomains((prev) => {
      const newDomains = renderedNodes.slice(0, 50);
      if (JSON.stringify(prev) !== JSON.stringify(newDomains)) {
        return newDomains;
      }
      return prev;
    });
  }, 150);

  // Filter events by date range only (used for stable per-kind counts)
  const dateFilteredEvents = useMemo(() => {
    if (!allEvents)
      return [];

    let events = allEvents as MonitorEvent[];

    if (startDate) {
      const startTs = startOfDay(startDate).getTime();
      events = events.filter((e) => e.createdAt >= startTs);
    }
    if (endDate) {
      const endTs = endOfDay(endDate).getTime();
      events = events.filter((e) => e.createdAt <= endTs);
    }

    return events;
  }, [allEvents, startDate, endDate]);

  // Apply the event-type (kind), TLD, and region selections on top of the date filter
  const filteredEvents = useMemo(() => {
    let events = dateFilteredEvents;

    if (selectedKinds.length > 0) {
      const kinds = new Set(selectedKinds);
      events = events.filter((e) => kinds.has(e.kind));
    }

    if (selectedTlds.length > 0) {
      const tlds = new Set(selectedTlds);
      events = events.filter((e) => tlds.has(e.domain.split('.').pop() ?? ''));
    }

    if (selectedRegions.length > 0) {
      events = events.filter((e) => {
        const country = domainStatusMap.get(e.domain)?.country ?? null;
        if (!country)
          return false;
        return selectedRegions.some((r) => isInRegion(country, r));
      });
    }

    return events;
  }, [dateFilteredEvents, selectedKinds, selectedTlds, selectedRegions, domainStatusMap]);

  // Transform to grid rows
  const rows = useMemo((): EventRow[] => {
    return filteredEvents.map((event) => {
      const domainStatus = domainStatusMap.get(event.domain);
      const overrideStatus = overrideMap.get(event.domain) ?? null;
      const hasActiveOverride = overrideMap.has(event.domain);

      return {
        id: event._id,
        domain: event.domain,
        kind: event.kind,
        fromStatus: event.fromStatus ?? null,
        toStatus: event.toStatus ?? null,
        message: event.message || '',
        createdAt: event.createdAt,
        timeAgo: formatDistanceToNow(new Date(event.createdAt), { addSuffix: true }),
        // Domain status fields
        state: domainStatus?.state ?? null,
        country: domainStatus?.country ?? null,
        registrationDate: domainStatus?.registrationDate ?? null,
        liveSince: domainStatus?.liveSince ?? null,
        launchVelocityDays: domainStatus?.launchVelocityDays ?? null,
        currentStatus: domainStatus?.currentStatus ?? 'error',
        hasActiveOverride,
        overrideStatus,
        // For 7-day history
        highWaterStatus: domainStatus?.highWaterStatus ?? null,
        graceExpiresAt: domainStatus?.graceExpiresAt ?? null,
      };
    });
  }, [filteredEvents, domainStatusMap, overrideMap]);

  // Facet counts derived from the date-filtered set (independent of facet selection)
  const kindCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const event of dateFilteredEvents) {
      counts[event.kind] = (counts[event.kind] || 0) + 1;
    }
    return counts;
  }, [dateFilteredEvents]);

  const tldCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const event of dateFilteredEvents) {
      const tld = event.domain.split('.').pop() ?? '';
      counts[tld] = (counts[tld] || 0) + 1;
    }
    return counts;
  }, [dateFilteredEvents]);

  const regionCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const event of dateFilteredEvents) {
      const country = domainStatusMap.get(event.domain)?.country ?? null;
      if (!country)
        continue;
      for (const r of REGIONS) {
        if (isInRegion(country, r.value)) {
          counts[r.value] = (counts[r.value] || 0) + 1;
        }
      }
    }
    return counts;
  }, [dateFilteredEvents, domainStatusMap]);

  // Facet chip handlers
  const toggleKind = useCallback((kind: EventKind) => {
    setSelectedKinds((prev) => toggleValue(prev, kind));
  }, []);

  const toggleTld = useCallback((tld: string) => {
    setSelectedTlds((prev) => toggleValue(prev, tld));
  }, []);

  const toggleRegion = useCallback((region: Region) => {
    setSelectedRegions((prev) => toggleValue(prev, region));
  }, []);

  const handleCopyCount = useCallback(() => {
    void navigator.clipboard.writeText(String(filteredEvents.length));
    setCopiedCount(true);
    setTimeout(() => setCopiedCount(false), 2000);
  }, [filteredEvents.length]);

  // Column definitions
  const columnDefs = useMemo((): ColDef<EventRow>[] => [
    {
      field: 'createdAt',
      headerName: 'Time',
      flex: 0,
      minWidth: 180,
      sortable: true,
      sort: 'desc',
      cellRenderer: (params: ICellRendererParams<EventRow>) => {
        if (!params.data)
          return null;
        return (
          <TooltipProvider>
            <Tooltip delayDuration={100}>
              <TooltipTrigger asChild>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">
                    {format(new Date(params.data.createdAt), 'MMM d, h:mm a')}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {params.data.timeAgo}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {format(new Date(params.data.createdAt), 'PPpp')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
    {
      field: 'kind',
      headerName: 'Event Type',
      flex: 0,
      minWidth: 160,
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams<EventRow>) => {
        if (!params.data)
          return null;
        return <EventKindBadge kind={params.data.kind} />;
      },
    },
    {
      field: 'domain',
      headerName: 'Domain',
      flex: 0,
      minWidth: 200,
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams<EventRow>) => (
        <Link
          to={`/app/domains/${params.value}`}
          className="font-medium text-primary hover:underline"
        >
          {params.value}
        </Link>
      ),
    },
    {
      field: 'fromStatus',
      headerName: 'Status Change',
      flex: 0,
      minWidth: 280,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<EventRow>) => {
        if (!params.data)
          return null;
        return (
          <StatusTransition
            from={params.data.fromStatus}
            to={params.data.toStatus}
          />
        );
      },
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 0,
      minWidth: 80,
      sortable: true,
      filter: true,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'country',
      headerName: 'Country',
      flex: 0,
      minWidth: 80,
      sortable: true,
      filter: true,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'registrationDate',
      headerName: 'Reg. Date',
      flex: 0,
      minWidth: 100,
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        if (!params.value)
          return '—';
        return format(new Date(params.value), 'MMM d, yyyy');
      },
    },
    {
      field: 'liveSince',
      headerName: 'Live Since',
      flex: 0,
      minWidth: 140,
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams<EventRow>) => (
        <LiveSinceCell
          domain={params.data?.domain || ''}
          liveSince={params.data?.liveSince ?? null}
          onUpdate={handleUpdateLiveSince}
        />
      ),
    },
    {
      field: 'launchVelocityDays',
      headerName: 'Velocity',
      flex: 0,
      minWidth: 100,
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams<EventRow>) => (
        <VelocityCell
          domain={params.data?.domain || ''}
          velocityDays={params.data?.launchVelocityDays ?? null}
          onUpdate={handleUpdateVelocity}
        />
      ),
    },
    {
      field: 'domain',
      headerName: '7-Day History',
      colId: 'history',
      flex: 0,
      minWidth: 160,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<EventRow>) => (
        <ScanHistoryCell
          domain={params.data?.domain || ''}
          highWaterStatus={params.data?.highWaterStatus ?? null}
          graceExpiresAt={params.data?.graceExpiresAt ?? null}
          currentStatus={params.data?.currentStatus || 'error'}
        />
      ),
    },
    {
      field: 'currentStatus',
      headerName: 'Status',
      flex: 0,
      minWidth: 200,
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams<EventRow>) => (
        <div className="flex h-full items-center gap-2">
          <StatusBadge status={params.value} />
          <a
            href={`https://${params.data?.domain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
            onClick={(e) => e.stopPropagation()}
          >
            <span>Visit</span>
            <ExternalLink className="size-3" />
          </a>
        </div>
      ),
    },
    {
      // Keyed on the status (not the boolean) so AG Grid sees a value change and
      // repaints the cell when an override switches from one status to another.
      field: 'overrideStatus',
      headerName: 'Override',
      flex: 0,
      minWidth: 130,
      sortable: true,
      filter: true,
      valueFormatter: (p) => (p.value ? String(p.value).replaceAll('_', ' ') : 'None'),
      cellRenderer: (params: ICellRendererParams<EventRow>) => (
        <OverrideCell
          domain={params.data?.domain || ''}
          hasOverride={params.data?.hasActiveOverride || false}
          overrideStatus={params.data?.overrideStatus ?? null}
          onSetOverride={handleSetOverride}
          onClearOverride={handleClearOverride}
        />
      ),
    },
    {
      headerName: 'Task',
      flex: 0,
      minWidth: 80,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<EventRow>) => {
        if (!params.data)
          return null;
        return (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenTaskDialog(params.data!.domain, params.data!.currentStatus);
            }}
          >
            <ListTodo className="size-4" />
            <span className="text-xs">Add</span>
          </Button>
        );
      },
    },
  ], [handleSetOverride, handleClearOverride, handleUpdateVelocity, handleUpdateLiveSince, handleOpenTaskDialog]);

  const handleGridReady = useCallback((params: { api: GridApi<EventRow> }) => {
    gridApiRef.current = params.api;
    registerGridApi(params.api);
    // Initial load of visible domains after grid renders
    setTimeout(updateVisibleDomains, 200);
  }, [registerGridApi, updateVisibleDomains]);

  const handleViewportChanged = useCallback(() => {
    updateVisibleDomains();
  }, [updateVisibleDomains]);

  // Initialize visible domains when rows first load
  useEffect(() => {
    if (rows.length > 0 && visibleDomains.length === 0) {
      const initialDomains = rows.slice(0, 50).map((r) => r.domain);
      setVisibleDomains(initialDomains);
    }
  }, [rows, visibleDomains.length]);

  // Update visible domains when the event-type selection changes
  useEffect(() => {
    if (gridApiRef.current && rows.length > 0) {
      setTimeout(updateVisibleDomains, 100);
    }
  }, [selectedKinds, rows, updateVisibleDomains]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedKinds([]);
    setSelectedTlds([]);
    setSelectedRegions([]);
    setStartDate(subDays(new Date(), 7));
    setEndDate(new Date());
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel(null);
    }
  };

  const getRowId = useCallback((params: { data: EventRow }) => params.data.id, []);

  // Export the currently filtered events to an Excel spreadsheet
  const handleExportReport = useCallback(() => {
    if (rows.length === 0) {
      toast.error('No events to export');
      return;
    }

    try {
      const formatStatus = (status: string | null) =>
        status ? status.replaceAll('_', ' ').replaceAll(/\b\w/g, (l) => l.toUpperCase()) : '';

      const exportRows = rows.map((row) => ({
        'Time': format(new Date(row.createdAt), 'MM/dd/yyyy h:mm a'),
        'Event Type': EVENT_KIND_CONFIG[row.kind]?.label ?? row.kind,
        'Domain': row.domain,
        'From Status': formatStatus(row.fromStatus),
        'To Status': formatStatus(row.toStatus),
        'Current Status': formatStatus(row.currentStatus),
        'State': row.state ?? '',
        'Country': row.country ?? '',
        'Registration Date': row.registrationDate ? format(new Date(row.registrationDate), 'MM/dd/yyyy') : '',
        'Live Since': row.liveSince ? format(new Date(row.liveSince), 'MM/dd/yyyy') : '',
        'Launch Velocity (days)': row.launchVelocityDays ?? '',
        'Override': row.hasActiveOverride ? formatStatus(row.overrideStatus) || 'Yes' : 'No',
        'Message': row.message,
      }));

      const wb = utils.book_new();
      const ws = utils.json_to_sheet(exportRows);

      ws['!cols'] = [
        { wch: 18 }, // Time
        { wch: 16 }, // Event Type
        { wch: 24 }, // Domain
        { wch: 18 }, // From Status
        { wch: 18 }, // To Status
        { wch: 18 }, // Current Status
        { wch: 8 }, // State
        { wch: 10 }, // Country
        { wch: 16 }, // Registration Date
        { wch: 12 }, // Live Since
        { wch: 18 }, // Launch Velocity
        { wch: 16 }, // Override
        { wch: 40 }, // Message
      ];

      utils.book_append_sheet(wb, ws, 'Monitor Events');

      const rangeLabel = startDate && endDate
        ? `${format(startDate, 'yyyyMMdd')}_${format(endDate, 'yyyyMMdd')}`
        : 'all_time';
      const filename = `monitor_events_${rangeLabel}_${format(new Date(), 'yyyyMMdd_HHmm')}.xlsx`;

      writeFile(wb, filename);
      toast.success(`Exported ${exportRows.length} events to ${filename}`);
    } catch {
      toast.error('Failed to export report');
    }
  }, [rows, startDate, endDate]);

  // Determine which preset (if any) matches the current date range
  const currentDatePreset = useMemo<DatePreset | 'custom'>(() => {
    if (!startDate && !endDate)
      return 'all';
    if (!startDate)
      return 'custom';
    const now = new Date();
    const start = startOfDay(startDate).getTime();
    if (start === startOfDay(now).getTime())
      return 'today';
    if (Math.abs(startDate.getTime() - subDays(now, 7).getTime()) < 86_400_000)
      return '7days';
    if (Math.abs(startDate.getTime() - subDays(now, 30).getTime()) < 86_400_000)
      return '30days';
    if (start === startOfMonth(now).getTime())
      return 'mtd';
    if (start === startOfYear(now).getTime())
      return 'ytd';
    return 'custom';
  }, [startDate, endDate]);

  // Quick date presets
  const setDatePreset = (preset: DatePreset) => {
    const now = new Date();
    switch (preset) {
      case 'today': {
        setStartDate(startOfDay(now));
        setEndDate(now);
        break;
      }
      case '7days': {
        setStartDate(subDays(now, 7));
        setEndDate(now);
        break;
      }
      case '30days': {
        setStartDate(subDays(now, 30));
        setEndDate(now);
        break;
      }
      case 'mtd': {
        setStartDate(startOfMonth(now));
        setEndDate(now);
        break;
      }
      case 'ytd': {
        setStartDate(startOfYear(now));
        setEndDate(now);
        break;
      }
      case 'all': {
        setStartDate(undefined);
        setEndDate(undefined);
        break;
      }
    }
  };

  const hasActiveFilters
    = selectedKinds.length > 0
      || selectedTlds.length > 0
      || selectedRegions.length > 0
      || searchQuery.trim() !== ''
      || currentDatePreset !== '7days';

  return (
    <Container className="pb-12">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Monitor Events</h1>
          <p className="mt-1 text-muted-foreground">
            Track and investigate domain status changes and activity
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportReport}
            disabled={rows.length === 0}
            className="gap-2"
          >
            <FileSpreadsheet className="size-4" />
            Export
            {' '}
            {filteredEvents.length.toLocaleString()}
            {' '}
            events
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/app/monitor">
              <Activity className="mr-2 size-4" />
              Back to Monitor
            </Link>
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <Card className="mb-6">
          <CardContent className="flex items-center justify-center py-12">
            <div className="flex items-center gap-3 text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
              <span>Loading events...</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      {!isLoading && (
        <Card className="mb-6 border-dashed">
          <CardContent className="py-4">
            <div className="flex flex-col gap-4">
              {/* Filter groups */}
              <div className="flex flex-wrap gap-x-8 gap-y-4">
                {/* Event Type */}
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Event Type
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() => setSelectedKinds([])}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
                        selectedKinds.length === 0
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground',
                      )}
                    >
                      All
                      <span className="tabular-nums opacity-70">{dateFilteredEvents.length}</span>
                    </button>
                    {EVENT_KIND_ORDER.map((kind) => {
                      const config = EVENT_KIND_CONFIG[kind];
                      const Icon = config.icon;
                      const isSelected = selectedKinds.includes(kind);
                      return (
                        <button
                          key={kind}
                          type="button"
                          onClick={() => toggleKind(kind)}
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full border border-transparent px-2.5 py-1 text-xs font-medium transition-all',
                            config.className,
                            isSelected
                              ? 'ring-2 ring-primary/50 ring-offset-1'
                              : 'opacity-60 hover:opacity-100',
                          )}
                        >
                          <Icon className="size-3" />
                          {config.label}
                          <span className="tabular-nums opacity-70">{kindCounts[kind] || 0}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* TLD */}
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    TLD
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {TLD_OPTIONS.map((opt) => {
                      const isSelected = selectedTlds.includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => toggleTld(opt.value)}
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
                            isSelected
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground',
                          )}
                        >
                          {opt.label}
                          <span className="tabular-nums opacity-70">{tldCounts[opt.value] || 0}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Region */}
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Region
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {REGIONS.map((opt) => {
                      const isSelected = selectedRegions.includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => toggleRegion(opt.value)}
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
                            isSelected
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground',
                          )}
                        >
                          {opt.label}
                          <span className="tabular-nums opacity-70">{regionCounts[opt.value] || 0}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Date Range */}
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Date Range
                  </span>
                  <div className="flex flex-wrap items-center gap-2">
                    <Select
                      value={currentDatePreset}
                      onValueChange={(val) => {
                        if (val !== 'custom')
                          setDatePreset(val as DatePreset);
                      }}
                    >
                      <SelectTrigger className="h-8 w-[150px] text-xs">
                        <div className="flex items-center gap-2">
                          <CalendarDays className="size-3.5 text-muted-foreground" />
                          <SelectValue placeholder="Date range" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {DATE_PRESET_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value} className="text-xs">
                            {option.label}
                          </SelectItem>
                        ))}
                        {currentDatePreset === 'custom' && (
                          <SelectItem value="custom" className="text-xs">Custom Range</SelectItem>
                        )}
                      </SelectContent>
                    </Select>

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 gap-2 text-xs font-normal">
                          <CalendarDays className="size-3.5" />
                          {formatDateRangeLabel(startDate, endDate)}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-4" align="start">
                        <div className="flex flex-col gap-3">
                          <div className="text-sm font-medium">Custom Date Range</div>
                          <div className="flex items-center gap-2">
                            <div className="flex flex-col gap-1">
                              <span className="text-xs text-muted-foreground">From</span>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button variant="outline" className="h-9 w-[130px] justify-start text-left font-normal">
                                    {startDate ? format(startDate, 'MMM d, yyyy') : 'Start date'}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <Calendar
                                    mode="single"
                                    selected={startDate}
                                    onSelect={setStartDate}
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                            </div>
                            <span className="mt-5 text-muted-foreground">→</span>
                            <div className="flex flex-col gap-1">
                              <span className="text-xs text-muted-foreground">To</span>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button variant="outline" className="h-9 w-[130px] justify-start text-left font-normal">
                                    {endDate ? format(endDate, 'MMM d, yyyy') : 'End date'}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <Calendar
                                    mode="single"
                                    selected={endDate}
                                    onSelect={setEndDate}
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                            </div>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>

              {/* Controls bar */}
              <div className="flex items-center justify-between gap-3 border-t pt-3">
                <div className="flex items-center gap-2">
                  {hasActiveFilters
                    ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearFilters}
                          className="gap-1.5 text-xs text-muted-foreground"
                        >
                          <RotateCcw className="size-3" />
                          Clear filters
                        </Button>
                      )
                    : (
                        <span className="text-xs text-muted-foreground">
                          Click an event type to filter, or adjust the date range
                        </span>
                      )}
                </div>

                <div className="flex items-center gap-3">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={handleCopyCount}
                          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
                        >
                          {copiedCount
                            ? (
                                <Check className="size-3.5 text-green-600" />
                              )
                            : (
                                <Copy className="size-3.5" />
                              )}
                          <span className="font-mono font-medium">{filteredEvents.length.toLocaleString()}</span>
                          <span>events</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Click to copy count</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <div className="relative w-56">
                    <Input
                      type="search"
                      placeholder="Search events by domain..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-8 pl-8 text-xs"
                    />
                    <Filter className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Grid */}
      {!isLoading && (
        <ScanHistoryContext.Provider value={scanHistoryMap}>
          <AGDataGrid
            columnDefs={columnDefs}
            rowData={rows}
            quickFilterText={searchQuery}
            getRowId={getRowId}
            onGridReady={handleGridReady}
            onViewportChanged={handleViewportChanged}
            onFilterChanged={handleViewportChanged}
            onSortChanged={handleViewportChanged}
            overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No events found for the selected filters</span>'
          />
        </ScanHistoryContext.Provider>
      )}

      {/* Task Creation Dialog */}
      {taskDialogData && (
        <CreateTaskDialog
          open={taskDialogOpen}
          onOpenChange={setTaskDialogOpen}
          domain={taskDialogData.domain}
          status={taskDialogData.status}
          context="monitor"
        />
      )}
    </Container>
  );
}

export default MonitorEventsPage;
